import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Settlement } from '~hooks/useSettlements/models';
import {
  formatSettlementTotal,
  getFriendlySettlementId,
  getSettlementCounterpartyName,
} from '~utils/settlements/settlement-utils';

// Light mode: header is a grey well on a white body. Dark mode: header is the lighter
// step above the dialog surface.
const tableHeaderCellSx = (theme: Theme) => ({
  bgcolor:
    theme.palette.mode === 'dark'
      ? theme.semantic.surfaceOverlay
      : theme.semantic.surfaceSunken,
  fontWeight: 600,
});

interface BulkExportSettlementsConfirmationDialogProps {
  category?: InvoiceCategoryFilter;
  excludedCount: number;
  exportableSettlements: Settlement[];
  isOpen: boolean;
  isSubmitting: boolean;
  maxCount: number;
  onClose: () => void;
  onConfirm: () => void;
}

export default function BulkExportSettlementsConfirmationDialog({
  category,
  excludedCount,
  exportableSettlements,
  isOpen,
  isSubmitting,
  maxCount,
  onClose,
  onConfirm,
}: BulkExportSettlementsConfirmationDialogProps) {
  const { currencyFormatter } = useCompanyCurrency();
  const exportableCount = exportableSettlements.length;
  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    onClose();
  }, [isSubmitting, onClose]);

  const hasExcludedSettlements = excludedCount > 0;
  const isOverLimit = exportableCount > maxCount;

  return (
    <Dialog isOpen={isOpen} maxWidth="sm" onClose={handleClose}>
      <DialogHeader onClose={handleClose}>
        {t('approvals.driver_pay.settlements.export_to_accounting.title')}
      </DialogHeader>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="error" sx={{ py: 0.5 }}>
            {t('approvals.driver_pay.settlements.export_to_accounting.warning')}
          </Alert>

          {hasExcludedSettlements && (
            <Alert
              data-test-id="settlement-bulk-export-excluded-warning"
              severity="warning"
              sx={{ py: 0.5 }}
            >
              {t('approvals.driver_pay.settlements.bulk_export_to_accounting.excluded', {
                count: excludedCount,
              })}
            </Alert>
          )}

          {isOverLimit && (
            <Alert
              data-test-id="settlement-bulk-export-over-limit-warning"
              severity="error"
              sx={{ py: 0.5 }}
            >
              {t(
                'approvals.driver_pay.settlements.bulk_export_to_accounting.over_limit',
                {
                  count: maxCount,
                },
              )}
            </Alert>
          )}

          <Typography data-test-id="settlement-bulk-export-message" variant="body2">
            {t('approvals.driver_pay.settlements.bulk_export_to_accounting.message', {
              count: exportableCount,
            })}
          </Typography>

          <TableContainer
            data-test-id="settlement-bulk-export-table"
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 320,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderCellSx}>
                    {t('approvals.driver_pay.settlements.settlement_id')}
                  </TableCell>
                  <TableCell sx={tableHeaderCellSx}>
                    {t('approvals.driver_pay.settlements.export_to_accounting.customer')}
                  </TableCell>
                  <TableCell align="right" sx={tableHeaderCellSx}>
                    {t('common.total')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exportableSettlements.map((settlement) => (
                  <TableRow key={settlement.id}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {getFriendlySettlementId(settlement, category)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {getSettlementCounterpartyName(settlement)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: 'success.main', fontWeight: 500 }}
                    >
                      {formatSettlementTotal(settlement, currencyFormatter)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSubmitting}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>
        <SmallLoadingButton
          color="primary"
          data-test-id="settlement-bulk-export-confirm-button"
          disabled={isOverLimit}
          loading={isSubmitting}
          onClick={onConfirm}
          variant="contained"
        >
          {t('approvals.driver_pay.settlements.export_to_accounting.export_button')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
